import { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Info } from "lucide-react";
import { useHomeIQ } from "@/homeiq/state/HomeIQContext";
import { fmtINR, fmtINRCompact } from "@/homeiq/services/format";
import { MARKET_DATA_SOURCE, MARKET_DATA_LAST_UPDATED } from "@/homeiq/services/placeholders";
import {
    SEGMENT_DEFS,
    SEGMENT_LABEL,
    psfForSegment,
    marketMatchesSegment,
} from "@/homeiq/data/segments";

const EASE = [0.16, 1, 0.3, 1];
const WEALTH_HORIZON_YEARS = 10; // fixed 10y projection now that the selector is removed

function Reveal({ children, delay = 0, className = "" }) {
    return (
        <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, delay, ease: EASE }}
            className={className}
        >
            {children}
        </motion.div>
    );
}

// ────────────────────────────────────────────────────────────────────
// InfoTooltip — hover on desktop, tap on mobile. Renders on top of
// everything else via absolute positioning inside a relatively-scoped
// wrapper. Auto-closes on outside click via a bubble handler.
// ────────────────────────────────────────────────────────────────────
function InfoTooltip({ label, testid }) {
    const [open, setOpen] = useState(false);
    return (
        <span
            className="relative inline-flex"
            onMouseEnter={() => setOpen(true)}
            onMouseLeave={() => setOpen(false)}
        >
            <button
                type="button"
                onClick={(e) => {
                    e.stopPropagation();
                    setOpen((v) => !v);
                }}
                onBlur={() => setOpen(false)}
                data-testid={testid}
                aria-label="More info"
                className="inline-flex items-center justify-center w-4 h-4 rounded-full text-[#0A1F44]/45 hover:text-[#1E6BFF] focus:outline-none focus:text-[#1E6BFF] transition-colors"
            >
                <Info className="w-3 h-3" strokeWidth={2.4} aria-hidden />
            </button>
            {open && (
                <span
                    role="tooltip"
                    className="absolute left-1/2 -translate-x-1/2 top-full mt-1.5 z-50 whitespace-nowrap rounded-lg bg-[#0A1F44] text-white text-[11px] font-medium px-2.5 py-1.5 shadow-[0_8px_24px_-8px_rgba(10,31,68,0.35)]"
                >
                    {label}
                </span>
            )}
        </span>
    );
}

// ────────────────────────────────────────────────────────────────────
// Metro status chip
// ────────────────────────────────────────────────────────────────────
const METRO_STYLE = {
    operational:        { label: "Metro · Operational",         dot: "#16a34a", text: "#15803d", border: "rgba(22,163,74,0.35)",  bg: "rgba(22,163,74,0.10)" },
    under_construction: { label: "Metro · Under Construction",  dot: "#d97706", text: "#b45309", border: "rgba(217,119,6,0.35)",  bg: "rgba(217,119,6,0.10)" },
    planned:            { label: "Metro · Planned",             dot: "#1E6BFF", text: "#1E6BFF", border: "rgba(30,107,255,0.30)", bg: "rgba(30,107,255,0.08)" },
    none:               { label: "No metro nearby",              dot: "#0A1F44", text: "#0A1F44", border: "rgba(10,31,68,0.15)",  bg: "rgba(10,31,68,0.05)" },
};

function MetroChip({ metro, size = "sm", showLine = false, testid }) {
    if (!metro) return null;
    const status = metro.status ?? "none";
    const tone = METRO_STYLE[status] ?? METRO_STYLE.none;
    const padding = size === "lg" ? "px-3 py-1 text-[11px]" : "px-2 py-0.5 text-[10px]";
    const labelBase = tone.label;
    const dateSuffix = metro.expectedOperational ? ` · ${metro.expectedOperational}` : "";
    const visibleLabel = showLine && metro.line
        ? `${labelBase} · ${metro.line}${dateSuffix}`
        : `${labelBase}${dateSuffix}`;
    return (
        <span
            data-testid={testid}
            className={`inline-flex items-center gap-1.5 rounded-full border font-semibold ${padding}`}
            style={{ backgroundColor: tone.bg, color: tone.text, borderColor: tone.border }}
            title={metro.line ?? undefined}
        >
            <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ backgroundColor: tone.dot }} aria-hidden />
            <span className="truncate">{visibleLabel}</span>
        </span>
    );
}

const RISK_STYLE = {
    Low:  { bg: "bg-[#16a34a]/10", text: "text-[#15803d]", border: "border-[#16a34a]/30" },
    Mid:  { bg: "bg-[#d97706]/10", text: "text-[#b45309]", border: "border-[#d97706]/30" },
    High: { bg: "bg-[#dc2626]/10", text: "text-[#dc2626]", border: "border-[#dc2626]/30" },
};

const CONF_STYLE = {
    High:   { bg: "bg-[#1E6BFF]/10", text: "text-[#1E6BFF]", border: "border-[#1E6BFF]/25" },
    Medium: { bg: "bg-[#0A1F44]/8",  text: "text-[#0A1F44]/75", border: "border-[#0A1F44]/15" },
    Low:    { bg: "bg-[#0A1F44]/5",  text: "text-[#0A1F44]/55", border: "border-[#0A1F44]/10" },
};

const STATUS_COLOR = {
    Within: { bg: "bg-[#16a34a]/10", text: "text-[#16a34a]", border: "border-[#16a34a]/30", dot: "bg-[#16a34a]" },
    Stretch: { bg: "bg-[#d97706]/10", text: "text-[#d97706]", border: "border-[#d97706]/30", dot: "bg-[#d97706]" },
    Beyond: { bg: "bg-[#dc2626]/10", text: "text-[#dc2626]", border: "border-[#dc2626]/30", dot: "bg-[#dc2626]" },
};

const STATUS_LABEL = {
    Within: "Within budget",
    Stretch: "Stretch",
    Beyond: "Beyond budget",
};

// ────────────────────────────────────────────────────────────────────
// Reclassify a market's status against the user's budget using the
// segment-adjusted PSF. Kept local to the Market tab — the underlying
// investment/affordability engines continue to use the mid band.
// ────────────────────────────────────────────────────────────────────
const STRETCH_PCT_MKT = 10;
function classifyForBudget(propertyCost, budget) {
    if (budget <= 0) return { status: "Beyond", gap: propertyCost };
    if (propertyCost <= budget) return { status: "Within", gap: propertyCost - budget };
    if (propertyCost <= budget * (1 + STRETCH_PCT_MKT / 100)) {
        return { status: "Stretch", gap: propertyCost - budget };
    }
    return { status: "Beyond", gap: propertyCost - budget };
}

// ────────────────────────────────────────────────────────────────────
// Budget Banner — counts now derive from the SAME filtered list the
// grid renders, honouring segment + zone + SBA + budget filter.
// ────────────────────────────────────────────────────────────────────
function BudgetBanner({ filtered }) {
    const { affordableBudget, marketOutputs } = useHomeIQ();
    const { market_score } = marketOutputs;
    const within = filtered.filter((m) => m.status === "Within").length;
    const stretch = filtered.filter((m) => m.status === "Stretch").length;
    const beyond = filtered.filter((m) => m.status === "Beyond").length;
    return (
        <Reveal>
            <div
                data-testid="market-budget-banner"
                className="rounded-3xl bg-[#0A1F44] text-white p-6 md:p-8 relative overflow-hidden"
            >
                <div className="absolute inset-0 blueprint-grid opacity-[0.06] pointer-events-none" />
                <div className="relative flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.25em] text-white/55 font-semibold">From your affordability</div>
                        <div className="mt-1 flex items-baseline gap-3 flex-wrap">
                            <span className="font-display font-black text-4xl md:text-5xl tracking-tight">{fmtINR(affordableBudget)}</span>
                            <span
                                data-testid="market-score-badge"
                                className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/8 border border-white/15 text-[11px] font-semibold text-white"
                            >
                                Market score · <span className="tabular-nums font-bold">{market_score}</span>
                            </span>
                        </div>
                        <div className="mt-1 text-sm text-white/55">Your target home value · used to colour each market below</div>
                    </div>
                    <div className="grid grid-cols-3 gap-2 text-[11px]">
                        <div data-testid="market-count-within" className="rounded-xl bg-white/5 border border-white/10 px-3 py-2 text-center">
                            <div className="flex items-center justify-center gap-1.5 text-white/65"><span className="w-2 h-2 rounded-full bg-[#16a34a]" />Within</div>
                            <div className="font-display font-extrabold text-lg tabular-nums">{within}</div>
                        </div>
                        <div data-testid="market-count-stretch" className="rounded-xl bg-white/5 border border-white/10 px-3 py-2 text-center">
                            <div className="flex items-center justify-center gap-1.5 text-white/65"><span className="w-2 h-2 rounded-full bg-[#d97706]" />Stretch</div>
                            <div className="font-display font-extrabold text-lg tabular-nums">{stretch}</div>
                        </div>
                        <div data-testid="market-count-beyond" className="rounded-xl bg-white/5 border border-white/10 px-3 py-2 text-center">
                            <div className="flex items-center justify-center gap-1.5 text-white/65"><span className="w-2 h-2 rounded-full bg-[#dc2626]" />Beyond</div>
                            <div className="font-display font-extrabold text-lg tabular-nums">{beyond}</div>
                        </div>
                    </div>
                </div>
            </div>
        </Reveal>
    );
}

// ────────────────────────────────────────────────────────────────────
// Filters
// ────────────────────────────────────────────────────────────────────
function PillGroup({ items, active, onSelect, testid, tooltipsByKey }) {
    return (
        <div className="flex flex-wrap gap-1.5">
            {items.map((it) => {
                const key = typeof it === "object" ? it.value : it;
                const label = typeof it === "object" ? it.label : it;
                const isActive = active === key;
                const tip = tooltipsByKey ? tooltipsByKey[key] : null;
                return (
                    <span key={key} className="inline-flex items-center gap-1">
                        <button
                            type="button"
                            onClick={() => onSelect(key)}
                            data-testid={`${testid}-${String(key).toLowerCase().replace(/\s+/g, "-")}`}
                            className={`px-3 py-1.5 rounded-full text-[11px] font-semibold transition-colors ${
                                isActive
                                    ? "bg-[#0A1F44] text-white"
                                    : "bg-[#0A1F44]/6 text-[#0A1F44]/70 hover:bg-[#0A1F44]/10"
                            }`}
                        >
                            {label}
                        </button>
                        {tip && <InfoTooltip label={tip} testid={`${testid}-info-${String(key).toLowerCase()}`} />}
                    </span>
                );
            })}
        </div>
    );
}

function Filters() {
    const {
        segment, setSegment, zone, setZone, sortBy, setSortBy, sba, setSba,
        budgetFilter, setBudgetFilter,
    } = useHomeIQ();
    const segItems = SEGMENT_DEFS.map((s) => ({ value: s.key, label: s.label }));
    const segTooltips = SEGMENT_DEFS.reduce((acc, s) => { acc[s.key] = s.tooltip; return acc; }, {});
    const zones = ["All", "North", "South", "East", "West", "Central"];
    const sorts = ["Affordable", "Growth", "Yield", "Growth Score"];
    const budgets = ["All", "Within", "Stretch", "Beyond"];

    return (
        <Reveal delay={0.05}>
            <div data-testid="market-filters" className="rounded-3xl border border-[#0A1F44]/10 bg-white p-5 md:p-6 space-y-5">
                {/* Row 1 — Segment · Zone · SBA · Sort */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.25em] text-[#0A1F44]/60 font-semibold mb-2">Segment</div>
                        <PillGroup items={segItems} active={segment} onSelect={setSegment} testid="mkt-seg" tooltipsByKey={segTooltips} />
                    </div>
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.25em] text-[#0A1F44]/60 font-semibold mb-2">Zone</div>
                        <select
                            value={zone}
                            onChange={(e) => setZone(e.target.value)}
                            data-testid="mkt-zone-select"
                            className="w-full bg-[#F7F8FB] rounded-full px-4 py-2 text-sm text-[#0A1F44] focus:outline-none focus:ring-2 focus:ring-[#1E6BFF]/30"
                        >
                            {zones.map((z) => <option key={z}>{z}</option>)}
                        </select>
                    </div>
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.25em] text-[#0A1F44]/60 font-semibold mb-2">
                            Saleable area · <span className="tabular-nums">{sba.toLocaleString("en-IN")} sqft</span>
                        </div>
                        <input
                            type="range"
                            min={800}
                            max={4000}
                            step={50}
                            value={sba}
                            onChange={(e) => setSba(Number(e.target.value))}
                            data-testid="mkt-sba-slider"
                            className="w-full accent-[#1E6BFF]"
                        />
                        <div className="flex justify-between text-[9px] tracking-wider text-[#0A1F44]/40 mt-1 tabular-nums">
                            <span>800</span>
                            <span>4,000 sqft</span>
                        </div>
                    </div>
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.25em] text-[#0A1F44]/60 font-semibold mb-2">Sort by</div>
                        <PillGroup items={sorts} active={sortBy} onSelect={setSortBy} testid="mkt-sort" />
                    </div>
                </div>

                {/* Row 2 — Budget filter (full width). Wealth-year selector removed. */}
                <div className="pt-2 border-t border-[#0A1F44]/8">
                    <div className="text-[10px] uppercase tracking-[0.25em] text-[#0A1F44]/60 font-semibold mb-2">Show markets</div>
                    <PillGroup items={budgets} active={budgetFilter} onSelect={setBudgetFilter} testid="mkt-budget" />
                </div>
            </div>
        </Reveal>
    );
}

// ────────────────────────────────────────────────────────────────────
// Market card — rich layout that surfaces every dataset field
// available for the market.
// ────────────────────────────────────────────────────────────────────
function LabelRow({ label, children, testid }) {
    return (
        <div data-testid={testid}>
            <div className="text-[9px] uppercase tracking-[0.2em] text-[#0A1F44]/45 font-semibold">{label}</div>
            <div className="mt-0.5 text-[12px] leading-snug text-[#0A1F44]">{children}</div>
        </div>
    );
}

function ChipList({ items, tone = "neutral", testid }) {
    if (!items || items.length === 0) return null;
    const cls = tone === "risk"
        ? "bg-[#dc2626]/6 text-[#b91c1c] border-[#dc2626]/20"
        : tone === "growth"
            ? "bg-[#16a34a]/8 text-[#15803d] border-[#16a34a]/25"
            : "bg-[#0A1F44]/5 text-[#0A1F44]/80 border-[#0A1F44]/12";
    return (
        <ul data-testid={testid} className="flex flex-wrap gap-1">
            {items.map((it, i) => (
                <li
                    key={`${it}-${i}`}
                    className={`px-2 py-0.5 rounded-full border text-[10px] leading-tight font-medium ${cls}`}
                >
                    {it}
                </li>
            ))}
        </ul>
    );
}

function MarketCard({ m, onClick, isPinned }) {
    const colors = STATUS_COLOR[m.status];
    const risk = RISK_STYLE[m.investmentRisk] || RISK_STYLE.Mid;
    const conf = CONF_STYLE[m.confidence] || CONF_STYLE.Medium;
    return (
        <button
            type="button"
            onClick={onClick}
            data-testid={`mkt-card-${m.id}`}
            className={`text-left rounded-2xl border bg-white p-4 hover:-translate-y-1 transition-all duration-500 group flex flex-col gap-3 ${
                isPinned
                    ? "border-[#16a34a] ring-2 ring-[#16a34a]/20 hover:border-[#16a34a]"
                    : "border-[#0A1F44]/10 hover:border-[#1E6BFF]/40"
            }`}
        >
            {/* Header — zone, name, growth score badge */}
            <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                    <div className="text-[10px] uppercase tracking-[0.25em] text-[#0A1F44]/55 font-semibold flex items-center gap-1.5">
                        {m.zone}
                        {isPinned && (
                            <span data-testid={`mkt-card-pinned-${m.id}`} className="px-1.5 py-0.5 rounded-full bg-[#16a34a] text-white text-[8px] font-bold uppercase tracking-wider">
                                Pinned
                            </span>
                        )}
                    </div>
                    <div className="mt-0.5 font-display font-extrabold text-lg text-[#0A1F44] leading-tight truncate">{m.area}</div>
                </div>
                <div
                    data-testid={`mkt-card-score-${m.id}`}
                    className="px-2 py-0.5 rounded-full bg-[#1E6BFF]/10 text-[#1E6BFF] text-[10px] font-bold tabular-nums shrink-0"
                    title="Growth Score"
                >
                    {m.score}
                </div>
            </div>

            {/* Estimated cost + status */}
            <div>
                <div className="text-[9px] uppercase tracking-[0.2em] text-[#0A1F44]/45 font-semibold">Estimated property cost</div>
                <div className="mt-0.5 flex items-baseline justify-between gap-2 flex-wrap">
                    <span className="font-display font-extrabold text-xl text-[#0A1F44] tabular-nums">{fmtINR(m.propertyCost)}</span>
                    <span
                        data-testid={`mkt-card-status-${m.id}`}
                        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] font-bold ${colors.bg} ${colors.text} ${colors.border}`}
                    >
                        <span className={`w-1.5 h-1.5 rounded-full ${colors.dot}`} aria-hidden />
                        {STATUS_LABEL[m.status]}
                    </span>
                </div>
                {m.status !== "Within" && (
                    <div
                        data-testid={`mkt-card-gap-${m.id}`}
                        className={`text-[10px] mt-1 tabular-nums font-semibold ${m.status === "Stretch" ? "text-[#d97706]" : "text-[#dc2626]"}`}
                    >
                        Budget gap · +{fmtINRCompact(m.budget_gap)} over budget
                    </div>
                )}
                {m.status === "Within" && m.budget_gap < 0 && (
                    <div
                        data-testid={`mkt-card-gap-${m.id}`}
                        className="text-[10px] mt-1 tabular-nums font-semibold text-[#16a34a]"
                    >
                        Headroom · {fmtINRCompact(Math.abs(m.budget_gap))} under budget
                    </div>
                )}
            </div>

            {/* Triple stat — PSF · CAGR · Yield */}
            <div className="grid grid-cols-3 gap-2 pt-2 border-t border-[#0A1F44]/6">
                <div>
                    <div className="text-[9px] uppercase tracking-[0.2em] text-[#0A1F44]/45 font-semibold">₹/sqft</div>
                    <div className="font-display font-extrabold text-sm text-[#0A1F44] tabular-nums">{m.displayPsf.toLocaleString("en-IN")}</div>
                    <div className="text-[9px] text-[#0A1F44]/55 uppercase tracking-wider">{m.displayBand}</div>
                </div>
                <div>
                    <div className="text-[9px] uppercase tracking-[0.2em] text-[#0A1F44]/45 font-semibold">CAGR</div>
                    <div className="font-display font-extrabold text-sm text-[#16a34a] tabular-nums">+{m.growthDisplay}</div>
                </div>
                <div>
                    <div className="text-[9px] uppercase tracking-[0.2em] text-[#0A1F44]/45 font-semibold">Yield</div>
                    <div className="font-display font-extrabold text-sm text-[#0A1F44] tabular-nums">{m.rentYield}%</div>
                    <div className="text-[9px] text-[#0A1F44]/55 tabular-nums">{fmtINRCompact(m.annualRental)}/yr</div>
                </div>
            </div>

            {/* Risk & Confidence pair */}
            <div className="grid grid-cols-2 gap-2">
                <div>
                    <div className="text-[9px] uppercase tracking-[0.2em] text-[#0A1F44]/45 font-semibold">Investment risk</div>
                    <span
                        data-testid={`mkt-card-risk-${m.id}`}
                        className={`inline-flex items-center gap-1 px-2 py-0.5 mt-0.5 rounded-full border text-[10px] font-bold ${risk.bg} ${risk.text} ${risk.border}`}
                    >
                        {m.investmentRisk}
                    </span>
                </div>
                <div>
                    <div className="text-[9px] uppercase tracking-[0.2em] text-[#0A1F44]/45 font-semibold">Confidence</div>
                    <span
                        data-testid={`mkt-card-confidence-${m.id}`}
                        className={`inline-flex items-center gap-1 px-2 py-0.5 mt-0.5 rounded-full border text-[10px] font-bold ${conf.bg} ${conf.text} ${conf.border}`}
                    >
                        {m.confidence}
                    </span>
                </div>
            </div>

            {/* Metro & road */}
            <div className="flex flex-col gap-1.5">
                <MetroChip metro={m.metro} testid={`mkt-card-metro-${m.id}`} />
                {m.roadConnectivity && (
                    <div data-testid={`mkt-card-roads-${m.id}`} className="text-[10px] text-[#0A1F44]/70">
                        <span className="text-[9px] uppercase tracking-[0.2em] text-[#0A1F44]/45 font-semibold mr-1">Roads:</span>
                        {m.roadConnectivity}
                    </div>
                )}
                {m.context?.itHub && (
                    <div data-testid={`mkt-card-ithub-${m.id}`} className="text-[10px] text-[#0A1F44]/70 leading-snug">
                        <span className="text-[9px] uppercase tracking-[0.2em] text-[#0A1F44]/45 font-semibold mr-1">IT hub:</span>
                        {m.context.itHub}
                    </div>
                )}
            </div>

            {/* Growth drivers */}
            {m.growthDrivers && m.growthDrivers.length > 0 && (
                <div>
                    <div className="text-[9px] uppercase tracking-[0.2em] text-[#16a34a]/70 font-semibold mb-1">Growth drivers</div>
                    <ChipList items={m.growthDrivers.slice(0, 4)} tone="growth" testid={`mkt-card-drivers-${m.id}`} />
                </div>
            )}

            {/* Risk factors */}
            {m.riskFactors && m.riskFactors.length > 0 && (
                <div>
                    <div className="text-[9px] uppercase tracking-[0.2em] text-[#dc2626]/70 font-semibold mb-1">Risk factors</div>
                    <ChipList items={m.riskFactors.slice(0, 3)} tone="risk" testid={`mkt-card-risks-${m.id}`} />
                </div>
            )}

            {/* Notes (rare — only markets where the master carries an editorial note) */}
            {m.notes && (
                <div
                    data-testid={`mkt-card-notes-${m.id}`}
                    className="text-[11px] text-[#0A1F44]/70 italic leading-snug border-l-2 border-[#0A1F44]/15 pl-2"
                >
                    {m.notes}
                </div>
            )}

            {/* Wealth projection · fixed 10-year horizon */}
            <div className="rounded-xl bg-[#F7F8FB] p-2.5 flex items-baseline justify-between gap-2 mt-auto">
                <div>
                    <div className="text-[9px] uppercase tracking-[0.2em] text-[#0A1F44]/45 font-semibold">In {WEALTH_HORIZON_YEARS} yrs</div>
                    <div className="font-display font-extrabold text-sm text-[#0A1F44] tabular-nums">{fmtINRCompact(m.futureValue)}</div>
                </div>
                <div className="text-right">
                    <div className="text-[9px] uppercase tracking-[0.2em] text-[#0A1F44]/45 font-semibold">Wealth created</div>
                    <div className="font-display font-extrabold text-sm text-[#16a34a] tabular-nums">+{fmtINRCompact(m.wealthCreated)}</div>
                </div>
            </div>

            <div className="flex items-center gap-2">
                <div className="flex-1 h-1 rounded-full bg-[#0A1F44]/8 overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-[#1E6BFF] to-[#5B8DFF]" style={{ width: `${m.score}%` }} />
                </div>
                <span className="text-[10px] text-[#0A1F44]/55 group-hover:text-[#1E6BFF]">Open →</span>
            </div>
        </button>
    );
}

// ────────────────────────────────────────────────────────────────────
// Single filtered pipeline used by BOTH banner counts and grid.
//   1. Segment inclusion (inventory rule)
//   2. Re-compute displayPsf, propertyCost, annualRental, futureValue,
//      wealthCreated, status, budget_gap FOR the selected segment.
//   3. Zone filter
//   4. Budget filter (Within/Stretch/Beyond)
//   5. Sort
// ────────────────────────────────────────────────────────────────────
function useFilteredMarkets() {
    const {
        marketOutputs, segment, zone, budgetFilter, sortBy, sba, affordableBudget,
    } = useHomeIQ();

    return useMemo(() => {
        const decorated = marketOutputs.decorated || [];
        // Step 1 · segment inclusion
        let list = decorated.filter((m) => marketMatchesSegment(m, segment));

        // Step 2 · re-derive per-market numbers using the segment's PSF band.
        list = list.map((m) => {
            const displayPsf = psfForSegment(m, segment);
            const propertyCost = (displayPsf ?? m.psf) * sba;
            const annualRental = propertyCost * (m.rentYield / 100);
            const futureValue = propertyCost * Math.pow(1 + m.growth / 100, WEALTH_HORIZON_YEARS);
            const wealthCreated = futureValue - propertyCost;
            const { status, gap } = classifyForBudget(propertyCost, affordableBudget);
            const bandLabels = { mid: "Mid band", premium: "Premium band", luxury: "Luxury band" };
            const bandKey = m.psfBands && m.psfBands.luxury === displayPsf
                ? "luxury"
                : m.psfBands && m.psfBands.premium === displayPsf
                    ? "premium"
                    : "mid";
            return {
                ...m,
                displayPsf: displayPsf ?? m.psf,
                displayBand: bandLabels[bandKey],
                propertyCost,
                annualRental,
                futureValue,
                wealthCreated,
                status,
                budget_gap: gap,
            };
        });

        // Step 3 · zone filter
        if (zone && zone !== "All") list = list.filter((m) => m.zone === zone);

        // Step 4 · budget filter
        if (budgetFilter && budgetFilter !== "All") list = list.filter((m) => m.status === budgetFilter);

        // Step 5 · sort
        if (sortBy === "Affordable") list.sort((a, b) => a.propertyCost - b.propertyCost);
        if (sortBy === "Growth") list.sort((a, b) => b.growth - a.growth);
        if (sortBy === "Yield") list.sort((a, b) => b.rentYield - a.rentYield);
        if (sortBy === "Growth Score") list.sort((a, b) => b.score - a.score);
        return list;
    }, [marketOutputs.decorated, segment, zone, budgetFilter, sortBy, sba, affordableBudget]);
}

function Grid({ filtered }) {
    const { setSpotlightMarket, pinnedMarketId, segment } = useHomeIQ();
    return (
        <Reveal delay={0.05}>
            <div className="space-y-4">
                <div className="flex items-baseline justify-between gap-4">
                    <h3 className="font-display font-extrabold text-xl md:text-2xl text-[#0A1F44]">
                        <span data-testid="market-grid-count">{filtered.length}</span>{" "}
                        <span className="text-[#0A1F44]/70 font-semibold">
                            {filtered.length === 1 ? "market" : "markets"} in {SEGMENT_LABEL[segment]}
                        </span>
                    </h3>
                    <span className="text-xs text-[#0A1F44]/55 hidden sm:inline">Tap any market for the full read</span>
                </div>
                <div data-testid="market-grid" className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                    {filtered.map((m) => (
                        <MarketCard
                            key={m.id}
                            m={m}
                            isPinned={pinnedMarketId === m.id}
                            onClick={() => setSpotlightMarket(m)}
                        />
                    ))}
                    {filtered.length === 0 && (
                        <div className="col-span-full rounded-2xl border border-dashed border-[#0A1F44]/15 p-8 text-center text-sm text-[#0A1F44]/55">
                            No markets match these filters. Try widening your zone, segment, or budget filter.
                        </div>
                    )}
                </div>
            </div>
        </Reveal>
    );
}

// ────────────────────────────────────────────────────────────────────
// Spotlight — same numbers the card shows (segment-adjusted).
// ────────────────────────────────────────────────────────────────────
function Spotlight() {
    const { spotlightMarket, setSpotlightMarket, pinnedMarketId, setPinnedMarketId, segment } = useHomeIQ();
    const isPinned = spotlightMarket && pinnedMarketId === spotlightMarket.id;
    const togglePin = () => {
        if (!spotlightMarket) return;
        setPinnedMarketId(isPinned ? null : spotlightMarket.id);
    };
    return (
        <AnimatePresence>
            {spotlightMarket && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    data-testid="market-spotlight"
                    className="fixed inset-0 z-50 flex items-end md:items-center justify-center p-4"
                >
                    <div onClick={() => setSpotlightMarket(null)} className="absolute inset-0 bg-[#0A1F44]/50 backdrop-blur-sm" />
                    <motion.div
                        initial={{ y: 40, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        exit={{ y: 40, opacity: 0 }}
                        transition={{ duration: 0.5, ease: EASE }}
                        className="relative bg-white rounded-3xl max-w-2xl w-full p-6 md:p-8 shadow-[0_30px_80px_-30px_rgba(10,31,68,0.4)] max-h-[90vh] overflow-y-auto"
                    >
                        <button
                            onClick={() => setSpotlightMarket(null)}
                            data-testid="market-spotlight-close"
                            className="absolute top-4 right-5 text-[#0A1F44]/50 hover:text-[#0A1F44] text-xl"
                            aria-label="Close"
                        >
                            ×
                        </button>

                        <p className="text-[10px] uppercase tracking-[0.25em] text-[#1E6BFF] font-semibold">
                            Bengaluru · {spotlightMarket.zone}
                        </p>
                        <h3 className="mt-1 font-display font-extrabold text-3xl md:text-4xl text-[#0A1F44]">{spotlightMarket.area}</h3>

                        <div className="mt-3 flex items-center gap-2 flex-wrap">
                            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border text-[11px] font-bold"
                                 style={{
                                     backgroundColor: spotlightMarket.status === "Within" ? "rgba(22,163,74,0.08)" : spotlightMarket.status === "Stretch" ? "rgba(217,119,6,0.08)" : "rgba(220,38,38,0.08)",
                                     color: spotlightMarket.status === "Within" ? "#16a34a" : spotlightMarket.status === "Stretch" ? "#d97706" : "#dc2626",
                                     borderColor: spotlightMarket.status === "Within" ? "rgba(22,163,74,0.3)" : spotlightMarket.status === "Stretch" ? "rgba(217,119,6,0.3)" : "rgba(220,38,38,0.3)",
                                 }}>
                                <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: spotlightMarket.status === "Within" ? "#16a34a" : spotlightMarket.status === "Stretch" ? "#d97706" : "#dc2626" }} aria-hidden />
                                {STATUS_LABEL[spotlightMarket.status]}
                            </div>
                            <MetroChip metro={spotlightMarket.metro} size="lg" showLine testid="mkt-spotlight-metro" />
                            <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-[#0A1F44]/6 border border-[#0A1F44]/12 text-[11px] font-semibold text-[#0A1F44]">
                                {SEGMENT_LABEL[segment]} · {spotlightMarket.displayBand}
                            </span>
                        </div>

                        {/* Wealth projection */}
                        <div data-testid="mkt-spotlight-wealth" className="mt-6 rounded-2xl bg-[#0A1F44] text-white p-5 relative overflow-hidden">
                            <div className="absolute inset-0 blueprint-grid opacity-[0.06] pointer-events-none" />
                            <div className="relative">
                                <div className="text-[10px] uppercase tracking-[0.25em] text-white/55 font-semibold">Wealth projection · {WEALTH_HORIZON_YEARS} years</div>
                                <div className="mt-3 grid grid-cols-3 gap-3">
                                    <div>
                                        <div className="text-[9px] uppercase tracking-[0.25em] text-white/55 font-semibold">Current value</div>
                                        <div className="mt-1 font-display font-extrabold text-lg tabular-nums">{fmtINR(spotlightMarket.propertyCost)}</div>
                                    </div>
                                    <div>
                                        <div className="text-[9px] uppercase tracking-[0.25em] text-white/55 font-semibold">Future value</div>
                                        <div className="mt-1 font-display font-extrabold text-lg tabular-nums">{fmtINR(spotlightMarket.futureValue)}</div>
                                    </div>
                                    <div>
                                        <div className="text-[9px] uppercase tracking-[0.25em] text-white/55 font-semibold">Wealth created</div>
                                        <div className="mt-1 font-display font-extrabold text-lg tabular-nums text-[#7FA8FF]">+{fmtINR(spotlightMarket.wealthCreated)}</div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Metric tiles */}
                        <div className="mt-5 grid grid-cols-2 md:grid-cols-3 gap-3">
                            {[
                                { k: "Price / sqft", v: `₹${spotlightMarket.displayPsf.toLocaleString("en-IN")}` },
                                { k: "CAGR", v: `+${spotlightMarket.growth}%` },
                                { k: "Rent yield", v: `${spotlightMarket.rentYield}%` },
                                { k: "Annual rental", v: fmtINRCompact(spotlightMarket.annualRental) },
                                { k: "Investment risk", v: spotlightMarket.investmentRisk },
                                { k: "Confidence", v: spotlightMarket.confidence },
                            ].map((s) => (
                                <div key={s.k} className="rounded-2xl bg-[#F7F8FB] p-4">
                                    <div className="text-[10px] uppercase tracking-[0.25em] text-[#0A1F44]/55 font-semibold">{s.k}</div>
                                    <div className="mt-1 font-display font-extrabold text-xl text-[#0A1F44]">{s.v}</div>
                                </div>
                            ))}
                        </div>

                        {/* Bengaluru context */}
                        <div data-testid="mkt-spotlight-context" className="mt-6 rounded-2xl border border-[#0A1F44]/10 p-5">
                            <div className="text-[10px] uppercase tracking-[0.25em] text-[#1E6BFF] font-semibold mb-3">— Bengaluru context</div>
                            <div className="space-y-2.5">
                                {[
                                    { k: "Metro", v: spotlightMarket.metro?.line ?? "—" },
                                    { k: "IT Hub", v: spotlightMarket.context?.itHub ?? "—" },
                                    { k: "Schools", v: spotlightMarket.context?.schools ?? "—" },
                                    { k: "Rental demand", v: spotlightMarket.context?.rentalDemand ?? "—" },
                                    { k: "Roads", v: spotlightMarket.roadConnectivity ?? "—" },
                                    { k: "Risk", v: spotlightMarket.investmentRisk },
                                ].map((row) => (
                                    <div key={row.k} className="flex items-baseline gap-3">
                                        <span className="w-28 shrink-0 text-[10px] uppercase tracking-[0.2em] text-[#0A1F44]/55 font-semibold">{row.k}</span>
                                        <span className="text-sm text-[#0A1F44]">{row.v}</span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Growth drivers & risk factors */}
                        {spotlightMarket.growthDrivers && spotlightMarket.growthDrivers.length > 0 && (
                            <div className="mt-5 rounded-2xl border border-[#16a34a]/20 bg-[#16a34a]/4 p-5">
                                <div className="text-[10px] uppercase tracking-[0.25em] text-[#15803d] font-semibold mb-2">Growth drivers</div>
                                <ChipList items={spotlightMarket.growthDrivers} tone="growth" testid="mkt-spotlight-drivers" />
                            </div>
                        )}
                        {spotlightMarket.riskFactors && spotlightMarket.riskFactors.length > 0 && (
                            <div className="mt-3 rounded-2xl border border-[#dc2626]/20 bg-[#dc2626]/4 p-5">
                                <div className="text-[10px] uppercase tracking-[0.25em] text-[#b91c1c] font-semibold mb-2">Risk factors</div>
                                <ChipList items={spotlightMarket.riskFactors} tone="risk" testid="mkt-spotlight-risks" />
                            </div>
                        )}
                        {spotlightMarket.notes && (
                            <div
                                data-testid="mkt-spotlight-notes"
                                className="mt-3 rounded-2xl border border-[#0A1F44]/12 bg-white p-4 text-sm text-[#0A1F44]/80 italic leading-relaxed"
                            >
                                {spotlightMarket.notes}
                            </div>
                        )}

                        {/* Pin CTA */}
                        <div className="mt-5 flex items-center justify-between gap-3 rounded-2xl border border-[#0A1F44]/10 bg-[#F7F8FB] p-4">
                            <div>
                                <div className="text-[10px] uppercase tracking-[0.25em] text-[#1E6BFF] font-semibold">Use for Investment math</div>
                                <p className="mt-1 text-xs text-[#0A1F44]/70 max-w-md">
                                    Pin this market so the Investment tab projects wealth using its {spotlightMarket.growthDisplay} CAGR.
                                </p>
                            </div>
                            <button
                                type="button"
                                onClick={togglePin}
                                data-testid="mkt-spotlight-pin"
                                className={`shrink-0 px-4 py-2 rounded-full text-xs font-semibold transition-colors ${
                                    isPinned
                                        ? "bg-[#16a34a] text-white hover:bg-[#15803d]"
                                        : "bg-[#0A1F44] text-white hover:bg-[#1E6BFF]"
                                }`}
                            >
                                {isPinned ? "✓ Pinned · Unpin" : "Pin this Market"}
                            </button>
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}

// ────────────────────────────────────────────────────────────────────
// Methodology & Sources — documents the FULL underlying dataset, so
// the table intentionally is NOT segment/zone/budget filtered.
// ────────────────────────────────────────────────────────────────────
function MethodologySources() {
    const { marketOutputs } = useHomeIQ();
    const [open, setOpen] = useState(false);
    return (
        <Reveal>
            <div data-testid="market-sources" className="rounded-3xl border border-[#0A1F44]/10 bg-white overflow-hidden">
                <button
                    type="button"
                    onClick={() => setOpen((v) => !v)}
                    data-testid="market-sources-toggle"
                    className="w-full flex items-baseline justify-between px-6 md:px-8 py-5 hover:bg-[#F7F8FB] transition-colors"
                >
                    <div className="text-left">
                        <div className="text-[10px] uppercase tracking-[0.25em] text-[#1E6BFF] font-semibold">— Methodology & Sources</div>
                        <div className="mt-1 font-display font-extrabold text-xl text-[#0A1F44]">
                            Where every number comes from
                        </div>
                    </div>
                    <span className="text-xs font-semibold text-[#1E6BFF]">{open ? "Hide ↑" : "Show ↓"}</span>
                </button>
                {open && (
                    <div className="border-t border-[#0A1F44]/8">
                        <div className="px-6 md:px-8 py-4 text-[11px] text-[#0A1F44]/65 flex flex-wrap gap-x-6 gap-y-1">
                            <span><strong className="text-[#0A1F44]">Source:</strong> {MARKET_DATA_SOURCE}</span>
                            <span><strong className="text-[#0A1F44]">Last updated:</strong> {MARKET_DATA_LAST_UPDATED}</span>
                        </div>
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead className="bg-[#0A1F44]/4 text-[#0A1F44]/65 text-[11px] uppercase tracking-[0.2em]">
                                    <tr>
                                        <th className="text-left px-6 md:px-8 py-3 font-semibold">Market</th>
                                        <th className="text-right px-4 py-3 font-semibold">PSF (₹)</th>
                                        <th className="text-right px-4 py-3 font-semibold">CAGR</th>
                                        <th className="text-right px-4 py-3 font-semibold">Yield</th>
                                        <th className="text-right px-4 py-3 font-semibold">Growth Score</th>
                                        <th className="text-left px-4 py-3 font-semibold">Source</th>
                                        <th className="text-right px-6 md:px-8 py-3 font-semibold">Last updated</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {marketOutputs.decorated.map((m) => (
                                        <tr key={m.id} data-testid={`market-source-row-${m.id}`} className="border-t border-[#0A1F44]/8">
                                            <td className="px-6 md:px-8 py-2.5 font-semibold text-[#0A1F44]">{m.area}</td>
                                            <td className="px-4 py-2.5 text-right tabular-nums text-[#0A1F44]">{m.psf.toLocaleString("en-IN")}</td>
                                            <td className="px-4 py-2.5 text-right tabular-nums text-[#16a34a]">+{m.growthDisplay}</td>
                                            <td className="px-4 py-2.5 text-right tabular-nums text-[#0A1F44]">{m.rentYield}%</td>
                                            <td className="px-4 py-2.5 text-right tabular-nums text-[#0A1F44]">{m.score}</td>
                                            <td className="px-4 py-2.5 text-[#0A1F44]/65">{MARKET_DATA_SOURCE}</td>
                                            <td className="px-6 md:px-8 py-2.5 text-right tabular-nums text-[#0A1F44]/65">{MARKET_DATA_LAST_UPDATED}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <p className="px-6 md:px-8 py-3 text-[11px] text-[#0A1F44]/55 italic border-t border-[#0A1F44]/8">
                            Indicative values from the HomeIQ Master Market Summary. External data partner integration is planned — figures will be refreshed when the live feed is connected.
                        </p>
                    </div>
                )}
            </div>
        </Reveal>
    );
}

// ────────────────────────────────────────────────────────────────────
// Page
// ────────────────────────────────────────────────────────────────────
export default function MarketIntelligenceTab() {
    const filtered = useFilteredMarkets();
    return (
        <section data-testid="market-tab" className="py-14 px-6 md:px-12 bg-white">
            <div className="max-w-7xl mx-auto space-y-8">
                <div className="mb-2">
                    <p className="text-[11px] tracking-[0.3em] uppercase text-[#1E6BFF] font-semibold mb-2">— Market intelligence</p>
                    <h2 className="font-display font-extrabold text-3xl sm:text-4xl text-[#0A1F44] tracking-tight">Where Bengaluru is moving.</h2>
                    <p className="mt-3 text-[#0A1F44]/65 max-w-2xl text-sm md:text-base">
                        Every micro-market scored against your budget, saleable area, and the segment you&apos;re shopping in.
                    </p>
                </div>
                <BudgetBanner filtered={filtered} />
                <Filters />
                <Grid filtered={filtered} />
                <MethodologySources />
                <Spotlight />
            </div>
        </section>
    );
}
